/**
 * Subscription plan types, enums, and static data.
 *
 * Ported from v1 SettingAccountSubscriptionPlans.ts.
 * Stripe payment links use environment variables.
 */

// ---------------------------------------------------------------------------
// Enums
// ---------------------------------------------------------------------------

export enum PaymentProvider {
  Stripe = "stripe",
  Apple = "apple",
  Google = "google",
  RevenueCat = "revenuecat",
  None = "none",
}

export enum PaymentTerm {
  Monthly = "monthly",
  Yearly = "yearly",
  Biennially = "biennially",
  Free = "free",
}

export enum PaymentPlan {
  Demo = "demo",
  Free = "free",
  Standard = "standard",
  Premium = "premium",
}

export type RevenueCatStore =
  | "AMAZON"
  | "APP_STORE"
  | "MAC_APP_STORE"
  | "PLAY_STORE"
  | "PROMOTIONAL"
  | "STRIPE"

// ---------------------------------------------------------------------------
// Pricing types
// ---------------------------------------------------------------------------

export interface PriceWithLink {
  price: number
  link: string
}

export interface Pricing {
  monthly: PriceWithLink
  yearly: PriceWithLink
  biennially: PriceWithLink
}

// ---------------------------------------------------------------------------
// Subscription interfaces
// ---------------------------------------------------------------------------

export interface CurrentSubscriptionDiscount {
  amount: number
  promotionCode?: string
  duration: "forever" | "once"
}

export interface CurrentSubscription {
  term?: PaymentTerm
  priceId?: string
  plan: PaymentPlan
  expires: string // UTC milliseconds as string
  active: boolean
  interval: string
  intervalCount: number
  amount: number
  currency: string
  provider: PaymentProvider
  paymentError: boolean
  paymentErrorInfo?: string
  subscriptionStatusInfo?: string
  discount?: CurrentSubscriptionDiscount
  // RevenueCat-specific
  isProduction?: boolean
  subscriptionProvider?: string
  subscriptionProviderId?: string
  store?: RevenueCatStore
}

export interface SubscriptionPlan {
  id: string
  name: string
  description?: string
  features: Record<string, string>
  pricing: Pricing
}

export interface SubscriptionSettings {
  id: string
  aiRequests: number
}

// ---------------------------------------------------------------------------
// Feature labels (for plan comparison table)
// ---------------------------------------------------------------------------

export const FEATURE_LABELS: Record<string, string> = {
  entries: "Number of tasks",
  backup: "Data backup",
  sync: "Synchronization",
  collaboration: "Collaboration",
  calendarIntegration: "Calendar integrations",
  mailToToToDo: "Email to To To-Do",
  devices: "Devices",
  images: "Images",
  sharableTemplates: "Sharable templates",
  aiRequests: "AI requests",
}

export const FEATURE_KEYS = Object.keys(FEATURE_LABELS)

// ---------------------------------------------------------------------------
// AI request limits per plan
// ---------------------------------------------------------------------------

export const SUBSCRIPTION_SETTINGS: SubscriptionSettings[] = [
  { id: "demo", aiRequests: 0 },
  { id: "free", aiRequests: 0 },
  { id: "standard", aiRequests: 100 },
  { id: "premium", aiRequests: 1000 },
]

// ---------------------------------------------------------------------------
// Plan data
// ---------------------------------------------------------------------------

// Payment links are now fetched from the server via GET /subscription/payment-link.
// The link fields below are kept for type compatibility but are not used directly.
// See src/features/settings/utils/fetchPaymentLink.ts for the server-based approach.

export const subscriptionPlans: SubscriptionPlan[] = [
  {
    id: "free",
    name: "Free",
    description:
      "For managing all aspects of your personal, family, and work life independently.",
    features: {
      entries: "Unlimited",
      backup: "—",
      sync: "—",
      collaboration: "—",
      calendarIntegration: "—",
      mailToToToDo: "—",
      devices: "1",
      images: "—",
      sharableTemplates: "—",
      aiRequests: "—",
    },
    pricing: {
      monthly: { price: 0, link: "" },
      yearly: { price: 0, link: "" },
      biennially: { price: 0, link: "" },
    },
  },
  {
    id: "standard",
    name: "Standard",
    description: "For getting family, work and personal life in sync.",
    features: {
      entries: "Unlimited",
      backup: "✓",
      sync: "✓",
      collaboration: "✓",
      calendarIntegration: "Unlimited",
      mailToToToDo: "✓",
      devices: "Unlimited",
      images: "—",
      sharableTemplates: "—",
      aiRequests: "100 / month",
    },
    pricing: {
      monthly: { price: 4.99, link: "" },
      yearly: { price: 53.88, link: "" },
      biennially: { price: 95.8, link: "" },
    },
  },
  {
    id: "premium",
    name: "Premium",
    description:
      "For complete management of your family, personal, and work life.",
    features: {
      entries: "Unlimited",
      backup: "✓",
      sync: "✓",
      collaboration: "✓",
      calendarIntegration: "Unlimited",
      mailToToToDo: "✓",
      devices: "Unlimited",
      images: "Unlimited",
      sharableTemplates: "Unlimited",
      aiRequests: "1000 / month",
    },
    pricing: {
      monthly: { price: 9.99, link: "" },
      yearly: { price: 107.89, link: "" },
      biennially: { price: 191.81, link: "" },
    },
  },
]

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

/** Format price as monthly cost string (e.g. "$4.49 / month") */
export function formatMonthlyPrice(
  plan: SubscriptionPlan,
  term: PaymentTerm,
  currency = "USD",
): string {
  const pricing = plan.pricing[term === PaymentTerm.Free ? "monthly" : term]
  if (!pricing || pricing.price === 0) return "Free"

  const divisor =
    term === PaymentTerm.Yearly ? 12 : term === PaymentTerm.Biennially ? 24 : 1
  const monthly = pricing.price / divisor

  const symbol = currency === "EUR" ? "€" : "$"
  return `${symbol}${monthly.toFixed(2)} / month`
}

/** Get the Stripe payment link for a plan + term */
export function getPaymentLink(
  plan: SubscriptionPlan,
  term: PaymentTerm,
): string {
  if (term === PaymentTerm.Free) return ""
  return plan.pricing[term]?.link ?? ""
}

/** Billing description (e.g. "billed annually") */
export function getBillingDescription(term: PaymentTerm): string {
  switch (term) {
    case PaymentTerm.Monthly:
      return "billed monthly"
    case PaymentTerm.Yearly:
      return "billed annually"
    case PaymentTerm.Biennially:
      return "billed every 2 years"
    case PaymentTerm.Free:
      return ""
  }
}
